/**
 * Results-page "SKU jump": when a keyword search (the Go button / Enter) is
 * an exact item number — or an exact Old Part Number
 * (custitem_old_part_number) — go straight to the product page instead of
 * showing the best-seller-ordered results list where the item may be buried.
 *
 * The results page keeps its "Best Sellers" default sort for every other
 * search, so "nozzle" still surfaces what Clemtex actually sells.
 *
 * How: on every Backbone route change, if the new fragment is a plain
 * "search?keywords=..." (no facets, sort or paging) and the keywords contain a
 * digit, a small relevance-sorted probe is sent to the Item Search API. The
 * API base URL and site parameters (c, n, country, currency, language,
 * pricelevel, use_pcv) are learned from the core's own Item Search calls via
 * an ajaxPrefilter, so nothing about the API is hard-coded. On a cold load of
 * the search page the Facets request fires inside the route callback, before
 * Backbone emits "route", so the template is always known by then.
 *
 * Route-driven rather than response-driven because Backbone.CachedSync serves
 * a repeated identical search from memory without any AJAX call.
 *
 * Navigation uses replace:true, so the results page never enters browser
 * history and Back returns to wherever the shopper searched from. Prefix
 * matches ("ct hdcbx360") do NOT jump. The results request is never delayed.
 * If two different items both match exactly (possible with old numbers),
 * the item-number match wins; two equal old-number matches do not jump.
 */
define('Clemtex.Search.SkuJump', [
    'Clemtex.Search.Utils',
    'Backbone',
    'jQuery',
    'underscore'
], function (Utils, Backbone, jQuery, _) {
    'use strict';

    var PROBE_LIMIT = 4;
    var PLAIN_SEARCH_FRAGMENT = /^search\/?\?keywords=([^&]*)$/i;
    var TEMPLATE_PARAMS = ['c', 'n', 'country', 'currency', 'language', 'pricelevel', 'use_pcv', 'nocache'];
    var apiTemplate = null;          // { base, params } learned from the core's Item Search calls
    var lastFragment = null;
    var installed = false;

    function learnTemplate(options) {
        var parsed = Utils.parseUrl(options.url);
        if (!parsed.base) { return; }
        apiTemplate = { base: parsed.base, params: _.pick(Utils.requestParams(options), TEMPLATE_PARAMS) };
    }

    // "search?keywords=ct%20hdcbx360-2" -> "ct hdcbx360-2"; null for anything else.
    function keywordsFromFragment(fragment) {
        var match = PLAIN_SEARCH_FRAGMENT.exec(fragment || '');
        if (!match) { return null; }
        var keywords;
        try { keywords = decodeURIComponent(match[1].replace(/\+/g, ' ')); } catch (e) { return null; }
        keywords = keywords.trim();
        return keywords || null;
    }

    function probeUrl(template, keywords) {
        return Utils.buildUrl(template.base, _.extend({}, template.params, {
            q: keywords,
            fieldset: 'typeahead',
            sort: 'relevance:desc',
            limit: PROBE_LIMIT,
            offset: 0
        }));
    }

    function productUrl(item) {
        var url = item && item.urlcomponent;
        if (!url) { return null; }
        return url.charAt(0) === '/' ? url : '/' + url;
    }

    // The single item to jump to, or null.
    function pickExactMatch(items, normalizedQuery) {
        var byItemNumber = _.filter(items, function (item) {
            return Utils.normalize(item.itemid) === normalizedQuery;
        });
        if (byItemNumber.length) { return byItemNumber[0]; }
        var byAltNumber = _.filter(items, function (item) {
            return !!Utils.matchingAltNumber(item, normalizedQuery);
        });
        return byAltNumber.length === 1 ? byAltNumber[0] : null;
    }

    function currentFragment() {
        return Backbone.history && Backbone.history.getFragment ? Backbone.history.getFragment() : '';
    }

    function check() {
        var fragment = currentFragment();
        if (fragment === lastFragment) { return; }   // same page, e.g. a duplicate route event
        lastFragment = fragment;

        var keywords = keywordsFromFragment(fragment);
        if (!keywords || !Utils.looksLikeItemNumber(keywords) || !apiTemplate) { return; }
        var normalizedQuery = Utils.normalize(keywords);
        if (normalizedQuery.length < Utils.MIN_MATCH_LENGTH) { return; }

        jQuery.ajax({
            url: probeUrl(apiTemplate, keywords),
            type: 'GET',
            dataType: 'json',
            clemtexPassthrough: true
        }).done(function (json) {
            var hit = pickExactMatch((json && json.items) || [], normalizedQuery);
            var url = hit && productUrl(hit);
            // Only act if the shopper is still looking at the same search.
            if (!url || currentFragment() !== fragment) { return; }
            Backbone.history.navigate(url, { trigger: true, replace: true });
        });
    }

    function safeCheck() {
        try { check(); } catch (e) {
            if (window.console) { window.console.error('ClemtexSearch SkuJump', e); }
        }
    }

    return {
        keywordsFromFragment: keywordsFromFragment,
        pickExactMatch: pickExactMatch,
        probeUrl: probeUrl,
        TEMPLATE_PARAMS: TEMPLATE_PARAMS,

        mountToApp: function mountToApp() {
            if (installed) { return; }
            installed = true;

            jQuery.ajaxPrefilter(function (options) {
                if (options.clemtexPassthrough || !Utils.isItemsApi(options.url)) { return; }
                try { learnTemplate(options); } catch (e) {}
            });

            if (Backbone && Backbone.history) {
                // Also fires for the initial route: SC starts Backbone.history
                // after all extensions have mounted.
                Backbone.history.on('route', safeCheck);
            }
        }
    };
});
